// src/utils/api.js

function stripQuotes(s) {
  return (s || "").trim().replace(/^['"]|['"]$/g, "");
}

function trimTrailingSlash(s) {
  return (s || "").replace(/\/+$/, "");
}

function getApiBase() {
  // Priority: env vars (CRA needs REACT_APP_ prefix)
  // Support multiple names to avoid breaking older setups.
  // Examples:
  // REACT_APP_SOLVIT_API_BASE=https://loopbox.cloud/solvit-api
  // REACT_APP_API_BASE_URL=https://loopbox.cloud/solvit-api
  const envCandidates = [
    process.env.REACT_APP_SOLVIT_API_BASE,
    process.env.REACT_APP_API_BASE_URL,
    process.env.REACT_APP_SOLVIT_API_BASE_URL,
  ];

  for (const v of envCandidates) {
    const cleaned = trimTrailingSlash(stripQuotes(v));
    if (cleaned) return cleaned;
  }

  // Local dev: React runs on localhost -> call the nginx host directly
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "https://loopbox.cloud/solvit-api";
  }

  // Production behind nginx (same origin)
  if (typeof window !== "undefined") {
    return `${window.location.origin}/solvit-api`;
  }

  // Fallback (SSR / tests)
  return "/solvit-api";
}

export async function apiFetch(path, options = {}) {
  const base = getApiBase();
  const urlPath = path.startsWith("/") ? path : `/${path}`;
  const url = `${base}${urlPath}`;

  const token = options.token || localStorage.getItem("solvit_auth_token");

  const headers = new Headers(options.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);

  // If caller passed a plain object body, send JSON (fixes text/plain issues)
  let body = options.body;
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (body !== undefined && body !== null && typeof body === "object" && !isFormData) {
    // If it's already a Blob/ArrayBuffer/etc, do NOT stringify
    const isBinary =
      (typeof Blob !== "undefined" && body instanceof Blob) ||
      (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer);

    if (!isBinary) {
      headers.set("Content-Type", "application/json");
      body = JSON.stringify(body);
    }
  }

  const res = await fetch(url, {
    ...options,
    headers,
    body,
    credentials: "include",
  });

  const contentType = (res.headers.get("content-type") || "").toLowerCase();

  // Read as text first so we can show good errors even if server returns HTML
  const raw = await res.text().catch(() => "");

  if (!res.ok) {
    // Try JSON {error:"..."}
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      const msg = parsed && typeof parsed === "object" && parsed.error ? parsed.error : raw;
      throw new Error(msg || `Request failed: ${res.status}`);
    } catch {
      throw new Error(raw || `Request failed: ${res.status}`);
    }
  }

  // Empty body (e.g. 204)
  if (!raw) return null;

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(raw);
    } catch {
      // fall through to raw
    }
  }

  return raw;
}
